import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useSearchParamsControls } from "@/search/Langston/useSearchParamsControls";
import { gradeLabelToValue } from "@/search/Langston/LangstonParameterSchema/LangstonParameterSchema";
import useStandardAggregationsQuery from "@/home/TeacherHomepage/services/useStandardAggregationsQuery";
import { useStandardsFetch } from "./hooks";
import {
  IStandardsSearchContext,
  standardsSearchProviderInitialValue,
  TOTAL_RESULTS_LIMIT_PER_PAGE,
} from "./StandardsSearchProvider.constants";

const StandardsSearchContext = createContext<IStandardsSearchContext>(
  standardsSearchProviderInitialValue,
);

export const useStandardsSearchContext = () =>
  useContext(StandardsSearchContext);

interface StandardsSearchProviderProps {
  children: ReactElement;
}

const StandardsSearchProvider = ({
  children,
}: StandardsSearchProviderProps) => {
  // For the standards search, we need to use the search params from the URL, this will be use as a fallback on useStandardsQuery
  const [searchParams, setSearchParams] = useState(new URLSearchParams());
  const { getSearchParamValues } = useSearchParamsControls({
    searchParams,
    setSearchParams,
  });

  const [standardsSearchOptions, setStandardsSearchOptions] = useState<{
    currentPage: number;
    currentNeedle: string;
    shouldFetchStandards: boolean;
  }>({
    currentPage: 1,
    currentNeedle: "",
    shouldFetchStandards: false,
  });

  const {
    allStandardsModalResults,
    selectedStandards,
    updateSelectedStandardsOnChange,
    top5Standards,
    totalStandardsResults,
    isLoadingStandardsResults,
    fetchFilteredStandards,
    standardsInQuery,
    searchParamsObj,
    resetStandardsList,
    setSelectedStandards,
    getTemporarySelectedFilters,
    clearTemporarySelectedFilters,
  } = useStandardsFetch(
    standardsSearchOptions.currentPage,
    standardsSearchOptions.currentNeedle,
    {
      searchParams,
      getSearchParamValues,
    },
  );

  const { data: standardAggregationsData } = useStandardAggregationsQuery();

  const standardAggregations = standardAggregationsData?.results || null;
  /**
   * Helper function to build an object with search parameters to feed
   * `fetchFilteredStandards`.
   */
  const buildFetchFilteredStandardsArg = (
    needle?: string,
    subject?: string,
    grade?: string,
    year?: string,
  ) => {
    const gradeSearchValue = gradeLabelToValue[grade ?? ""];
    const hasValue = (v?: string) => v !== undefined && v !== "-";
    return {
      standard_needle: needle,
      ...(hasValue(subject) && { subjects: subject }),
      ...(hasValue(gradeSearchValue) && { grade_level: gradeSearchValue }),
      ...(hasValue(year) && { adoption_years: year }),
    };
  };

  const handleViewMoreStandards = useCallback(
    (needle?: string, subject?: string, grade?: string, year?: string) => {
      const { currentPage } = standardsSearchOptions;
      setStandardsSearchOptions({
        ...standardsSearchOptions,
        currentPage: currentPage + 1,
      });
      fetchFilteredStandards({
        page: currentPage,
        ...buildFetchFilteredStandardsArg(needle, subject, grade, year),
      });
    },
    [searchParamsObj, standardsSearchOptions],
  );

  const handleStandardsSearch = useCallback(
    (needle?: string, subject?: string, grade?: string, year?: string) => {
      setStandardsSearchOptions({
        ...standardsSearchOptions,
        currentNeedle: needle || "",
        currentPage: 1,
      });
      fetchFilteredStandards({
        page: 0,
        ...buildFetchFilteredStandardsArg(needle, subject, grade, year),
      });
    },
    [searchParamsObj, standardsSearchOptions],
  );

  const resetStandardsSearch = () => {
    resetStandardsList();
    setSelectedStandards([]);
    setStandardsSearchOptions({
      currentNeedle: "",
      currentPage: 1,
      shouldFetchStandards: false,
    });
  };

  const showViewMoreButton = useMemo(
    () =>
      allStandardsModalResults &&
      !isLoadingStandardsResults &&
      standardsSearchOptions.currentPage <
        Math.ceil(totalStandardsResults / TOTAL_RESULTS_LIMIT_PER_PAGE),
    [
      allStandardsModalResults,
      standardsSearchOptions.currentPage,
      isLoadingStandardsResults,
      totalStandardsResults,
    ],
  );

  return (
    <StandardsSearchContext.Provider
      value={{
        top5Standards,
        selectedStandards,
        updateSelectedStandardsOnChange,
        allStandardsModalResults,
        standardsInQuery,
        totalStandardsResults,
        fetchFilteredStandards,
        isLoadingStandardsResults,
        handleViewMoreStandards,
        currentNeedle: standardsSearchOptions.currentNeedle,
        currentPage: standardsSearchOptions.currentPage,
        handleStandardsSearch,
        resetStandardsSearch,
        showViewMoreButton,
        setSelectedStandards,
        getTemporarySelectedFilters,
        clearTemporarySelectedFilters,
        standardAggregations,
      }}
    >
      {children}
    </StandardsSearchContext.Provider>
  );
};

export { StandardsSearchProvider };
